/**
 * Where the person left the app's popups, as a distance from the place a popup opens
 * in (the centre of the window). One preference for every popup, kept in this browser:
 * it is a convenience of the person at this screen, never something the server knows.
 */
export interface PopupOffset {
  x: number;
  y: number;
}

export interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface Size {
  width: number;
  height: number;
}

export const CENTERED: PopupOffset = { x: 0, y: 0 };
export const POPUP_OFFSET_KEY = 'qmi.popupOffset';

/** A popup never sits closer to the edge of the window than this. */
const EDGE_GAP_PX = 8;

export function isCentered(offset: PopupOffset): boolean {
  return offset.x === 0 && offset.y === 0;
}

export function sameOffset(a: PopupOffset, b: PopupOffset): boolean {
  return a.x === b.x && a.y === b.y;
}

/** What the browser kept, or the centre when it kept nothing readable. */
export function parseOffset(raw: string | null): PopupOffset {
  if (!raw) return CENTERED;
  try {
    const kept: unknown = JSON.parse(raw);
    if (typeof kept !== 'object' || kept === null) return CENTERED;
    const { x, y } = kept as Record<string, unknown>;
    if (typeof x !== 'number' || typeof y !== 'number') return CENTERED;
    if (!Number.isFinite(x) || !Number.isFinite(y)) return CENTERED;
    return { x, y };
  } catch {
    return CENTERED;
  }
}

// Storage can be missing or refuse (private window, blocked site data): the popup
// then simply opens centred and forgets where it was moved to.
export function readOffset(): PopupOffset {
  try {
    return parseOffset(globalThis.localStorage.getItem(POPUP_OFFSET_KEY));
  } catch {
    return CENTERED;
  }
}

export function saveOffset(offset: PopupOffset): void {
  try {
    if (isCentered(offset)) globalThis.localStorage.removeItem(POPUP_OFFSET_KEY);
    else globalThis.localStorage.setItem(POPUP_OFFSET_KEY, JSON.stringify(offset));
  } catch {
    // Nothing to do: the position is a convenience.
  }
}

function clampAxis(wanted: number, restStart: number, size: number, available: number): number {
  const lowest = EDGE_GAP_PX - restStart;
  const highest = available - EDGE_GAP_PX - size - restStart;
  // Larger than the window on this axis: it stays where the page lays it out.
  if (lowest > highest) return 0;
  return Math.min(Math.max(wanted, lowest), highest);
}

/**
 * The closest offset to `wanted` that keeps the whole popup inside the window.
 * `atRest` is where the popup sits when nobody has moved it. A position kept on a
 * larger screen is pulled back in, so a popup can never open out of reach.
 */
export function clampOffset(wanted: PopupOffset, atRest: Box, viewport: Size): PopupOffset {
  return {
    x: clampAxis(wanted.x, atRest.left, atRest.width, viewport.width),
    y: clampAxis(wanted.y, atRest.top, atRest.height, viewport.height),
  };
}
